/**
 * The lab's consortia and works (proposals and publications), most recently active first: the latest 5 on
 * the page, the whole list in a dialog. Backend: GET /consortium/labs/<id>/work/, which already filters by
 * viewer: the PI sees everything of the lab's members, a member their own, anyone else the public consortia.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, AvatarGroup, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { labAvatarSx } from './labTheme';

export const WORK_SHOWN = 5;

const ago = (iso) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

function People({ people, lab }) {
  if (!people?.length) return null;
  return (
    <Tooltip title={people.map((p) => p.name).filter(Boolean).join(', ')}>
      <AvatarGroup max={4} sx={{ flexShrink: 0, '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 10.5, borderWidth: 1.5, ...labAvatarSx(lab) } }}>
        {people.map((p) => <Avatar key={p.id} src={p.avatar_url || undefined} alt={p.name}>{(p.name || '?')[0]}</Avatar>)}
      </AvatarGroup>
    </Tooltip>
  );
}

function Row({ title, sub, people, lab, onOpen, locked }) {
  return (
    <Box onClick={onOpen} role={onOpen ? 'link' : undefined} tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.9, cursor: onOpen ? 'pointer' : 'default',
        borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 'none' },
        '&:hover .title': onOpen ? { textDecoration: 'underline' } : undefined }}>
      <Box flex={1} minWidth={0}>
        <Typography className="title" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {title}
          {locked && (
            <Tooltip title="You don't take part in it, so you can't open it">
              <LockIcon sx={{ fontSize: 13, ml: 0.5, verticalAlign: '-2px', color: 'text.disabled' }} />
            </Tooltip>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>{sub}</Typography>
      </Box>
      <People people={people} lab={lab} />
    </Box>
  );
}

function ListWithMore({ rows, render, title }) {
  const [all, setAll] = useState(false);
  return (
    <Box>
      {rows.slice(0, WORK_SHOWN).map(render)}
      {rows.length > WORK_SHOWN && (
        <Button size="small" onClick={() => setAll(true)} sx={{ mt: 0.5, px: 0 }}>Show all {rows.length}</Button>
      )}
      <Dialog open={all} onClose={() => setAll(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>{title} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{rows.length}</Box></DialogTitle>
        <DialogContent dividers>{rows.map(render)}</DialogContent>
        <DialogActions><Button onClick={() => setAll(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export function LabConsortiaBody({ lab, consortia }) {
  const navigate = useNavigate();
  return (
    <ListWithMore rows={consortia} title={`${lab.name}: consortia`} render={(c) => (
      <Row key={c.id} lab={lab} people={c.people} locked={!c.can_open}
        onOpen={c.can_open ? () => navigate(`/consortium/${c.id}/`) : undefined}
        title={c.name}
        sub={[c.title, c.status && c.status[0].toUpperCase() + c.status.slice(1), !c.is_public && 'members only',
          `active ${ago(c.last_active)}`].filter(Boolean).join(' · ')} />
    )} />
  );
}

export function LabWorksBody({ lab, works }) {
  const navigate = useNavigate();
  return (
    <ListWithMore rows={works} title={`${lab.name}: works`} render={(w) => {
      const isPub = w.document_type === 'publication';
      return (
        <Row key={w.id} lab={lab} people={w.people} locked={!w.can_open}
          onOpen={w.can_open ? () => navigate(`/consortium/${w.consortium}/${isPub ? 'publications' : 'proposals'}/${w.id}/`) : undefined}
          title={<>
            {w.acronym && <Box component="span" sx={{ mr: 0.75 }}>{w.acronym}</Box>}
            <Box component="span" sx={{ fontWeight: w.acronym ? 400 : 600 }}>{(w.title || '').trim() || 'Untitled'}</Box>
            <Chip size="small" label={isPub ? 'Publication' : 'Proposal'} color={isPub ? 'secondary' : 'primary'}
              variant="outlined" sx={{ height: 18, fontSize: '0.65rem', ml: 0.75, verticalAlign: '1px' }} />
          </>}
          sub={[w.consortium_name, `edited ${ago(w.last_edited)}`].join(' · ')} />
      );
    }} />
  );
}
